import { z } from 'zod';
import { CAPABILITIES, getRoleByHandle } from '../constants.js';
import { PAT_TIER_NAMES } from '../pat-tiers.js';
// PAT create payload. See docs/proposals/0.1.39-pat-scope-tiers.md §4.5.
//
// 0.1.39: every mint must declare a `tier`. Named tiers expand from the
// shared PAT_TIER_DEFAULTS registry (optionally overridden by
// apps/api/config/pat-tiers.json); the `custom` tier uses the caller's
// own `scopes[]` array.
//
// Hard-reject rule: named tier + non-empty `scopes[]` is a 422. Silent
// scope-drop under a named tier is exactly the ambiguity this proposal
// exists to eliminate. See §4.5 Rev 2 note.
//
// 0.1.43 adds sourceTag — the session-attribution tag stamped onto
// downstream writes (work_item_activity.source, work_item_comments.source)
// by Phase B middleware. See CTSK-154.
//
// expiresAt is optional — null/undefined = no expiry. Scopes strings
// aren't zod-enum'd against CAPABILITIES here because we want a friendly
// per-scope 422 from the route (via intersection filtering) instead of
// a noisy union-discriminator error from zod.
/**
 * Session attribution regex — kebab-case, letter-first, letter-or-digit-last,
 * hyphens only allowed inside. 2-64 chars total.
 *
 * Used for the `sourceTag` field on PATs (0.1.43+). Matches the session-tag
 * naming convention documented in Mission Control's `SESSION_REGISTRY.md`
 * (e.g. `mindmap-cards-review`, `review-ct`, `vault-orchestrator`).
 *
 * Explicit constraints:
 *   - Starts with a lowercase letter (avoids leading digit / hyphen edge cases).
 *   - Ends with a lowercase letter or digit (avoids trailing hyphen).
 *   - Internal chars: lowercase letters, digits, hyphens.
 *   - Length: 2-64 chars (min-2 falls out of "first + last char" requirement).
 */
export const SESSION_TAG_REGEX = /^[a-z][a-z0-9-]{0,62}[a-z0-9]$/;
/**
 * Strict (CTSK-445) — unknown keys are rejected with 400
 * VALIDATION_ERROR naming the offending key, instead of Zod's default
 * silent strip. This schema is the CTSK-427 root-cause site: the
 * silent strip of `expiresInDays` (before that key existed here)
 * null-expired every script-minted PAT for months (81 orphans; memory
 * feedback_zod_field_name_silent_drop). Note: .strict() sits on the
 * inner z.object — .superRefine below wraps it in ZodEffects.
 */
export const PersonalAccessTokenCreateSchema = z
    .object({
    name: z.string().trim().min(1).max(200),
    /**
     * Semantic tier. Must be one of PAT_TIER_NAMES. Non-`custom` values
     * expand from PAT_TIER_DEFAULTS at mint time; `custom` requires an
     * explicit `scopes[]` array.
     */
    tier: z.enum(PAT_TIER_NAMES),
    /**
     * Only permitted when `tier === 'custom'`. Any non-empty value with
     * a named tier is a hard 422.
     */
    scopes: z.array(z.string().min(1).max(64)).min(1).max(64).optional(),
    /**
     * Session attribution tag (0.1.43+). Set at mint time; not editable
     * after. Server auto-stamps this on every write made with the token
     * (work_item_activity.source, work_item_comments.source) via Phase B
     * middleware, giving unforgeable per-session attribution when multiple
     * sessions share one service user.
     *
     * Kebab-case per SESSION_TAG_REGEX. Optional — omit for untagged writes
     * (the pre-0.1.43 default behavior).
     */
    sourceTag: z.string().regex(SESSION_TAG_REGEX).optional(),
    /**
     * CTSK-250 (0.1.94+) — the ROLE_CATALOG handle the mint UI seeded
     * `scopes[]` from (PatsPanel "Role (from catalog)" dropdown).
     * Preserved on the row for the audit trail ("minted as role X") AND,
     * since CTSK-409 (2026-08-21), as the live re-derivation key: on
     * every auth check, resolveBearerSession looks this handle up in
     * the CURRENT ROLE_CATALOG and uses its capabilities as the
     * authoritative grant (falling back to the frozen scopes[] snapshot
     * only if the handle no longer resolves). This mint-time cap
     * intersection is still enforced below so a PAT can never be minted
     * wider than the caller — sourceRole doesn't bypass that — but a
     * LATER server-side catalog update to this same handle now DOES
     * flow through to already-minted rows without a re-mint.
     *
     * Only permitted when `tier === 'custom'` — the role dropdown always
     * flips the tier to custom when it seeds. Must resolve to a current
     * catalog handle other than 'custom' (the "custom (no template)"
     * dropdown entry means "no source role", so the UI omits this field
     * entirely on that path). Enforced in superRefine below.
     */
    // CTSK-312 Slice 2 (ADR 0002 §3 Q3, §4 Slice 2 line) — a sourceRole
    // may ALSO be a qualified `<nest-slug>/<handle>` referring to a
    // nest-scoped integration template (nest_integration_templates),
    // instead of a bare ROLE_CATALOG handle. The combined regex accepts
    // either shape in one pass: bare (`^[a-z0-9][a-z0-9-]*$`) or
    // qualified with exactly one `/` and no leading/trailing/double
    // slash (`^[a-z0-9][a-z0-9-]*\/[a-z0-9][a-z0-9-]*$`) — each half
    // uses the same charset as Slice 1's NestIntegrationTemplateSchema
    // `handle` field. Existence of the referenced nest/template is
    // resolved SERVER-SIDE at mint time (apps/api/src/routes/pats.ts) —
    // this schema layer never hits the DB.
    sourceRole: z
        .string()
        .min(1)
        .max(64)
        .regex(/^[a-z0-9][a-z0-9-]*(\/[a-z0-9][a-z0-9-]*)?$/, 'sourceRole must be a lowercase-kebab ROLE_CATALOG handle, optionally qualified as ' +
        '<nest-slug>/<handle> for a nest-scoped integration template (each half starting ' +
        'with a letter or digit, no "/" inside either half)')
        .optional(),
    /**
     * Optional explicit org lock (0.1.54+, CTSK-169). When present, the
     * mint route validates the caller is a member of this org and stamps
     * `personal_access_tokens.mint_org_id`. When absent, the server picks
     * a default:
     *   - cookie session → req.session.activeOrgId (the switcher's value)
     *   - Bearer session → caller PAT's own mint_org_id (transitive lock)
     *
     * CTSK-455 Phase 4 (2026-09-04) — that "stays NULL" fallback above
     * is no longer true. If both the payload and the caller session
     * leave orgId unresolvable, `POST /pats` now returns 400
     * ORG_REQUIRED_FOR_PAT_MINT BEFORE calling mint_pat() (which would
     * also reject a NULL org, at the DB layer, as a backstop). This
     * field deliberately STAYS optional here rather than becoming
     * required (a documented deviation from one option the brief
     * offered): the route's session-derived default — cookie
     * `activeOrgId` / Bearer caller's own `mintOrgId` — is the normal,
     * expected way most callers resolve an org, and making this field
     * required would force every caller to look up and pass their own
     * active org explicitly even when the session already knows it.
     * "Org required" is enforced as an INVARIANT on the resolved value,
     * not as a requirement that the caller supply it explicitly.
     *
     * ULID format (26 uppercase Base32 chars).
     */
    orgId: z.string().length(26).optional(),
    /**
     * Optional explicit expiry. CTSK-427 (2026-08-26): omitting this
     * field no longer means "no expiry" unconditionally — see the
     * mint route (apps/api/src/routes/pats.ts) for the full
     * precedence order (explicit expiresAt > expiresInDays > role-based
     * default). An explicit value here — including explicit `null` —
     * always wins over any default; that stays the deliberate no-expiry
     * escape hatch for long-lived-tier callers.
     */
    expiresAt: z
        .union([
        z.string().datetime({ offset: true }),
        z.literal('').transform(() => null),
        z.null(),
    ])
        .optional(),
    /**
     * CTSK-427 (2026-08-26) — alternative to `expiresAt`: a relative TTL
     * in days, mirroring the shape `approve-and-mint-pat.ts`'s
     * `mintPolicy.expiresInDays` already uses. Added because
     * `scripts/mint_session_pats.sh`'s direct-flow was POSTing this
     * exact field name to this exact route and getting it silently
     * stripped (this schema had no such key) — every script-minted PAT
     * landed with a null expiry regardless of the operator's
     * `--expires-in-days` flag. Ignored when `expiresAt` is also
     * present (explicit timestamp wins).
     */
    expiresInDays: z.number().int().positive().max(3650).optional(),
})
    .strict()
    .superRefine((val, ctx) => {
    // Custom tier requires an explicit scope list.
    if (val.tier === 'custom' && (!val.scopes || val.scopes.length === 0)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['scopes'],
            message: 'scopes is required when tier is "custom"',
        });
    }
    // Named tier + non-empty scopes is a HARD reject. This is
    // deliberate — accept-but-ignore would let an operator send
    // scopes[] alongside tier: 'admin' and believe they had narrowed
    // the grant, when in fact the full admin scope set would be
    // minted. Silent ambiguity in an auth surface is exactly what
    // this proposal exists to prevent.
    if (val.tier !== 'custom' && val.scopes && val.scopes.length > 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['scopes'],
            message: 'scopes array is only permitted when tier is "custom"',
        });
    }
    // CTSK-250 — sourceRole is an annotation on the role-dropdown mint
    // path, which always runs through tier=custom. A named tier +
    // sourceRole is incoherent (the tier expansion, not the role,
    // determined the scopes) — hard-reject like the scopes rule above
    // rather than accept-but-ignore.
    if (val.sourceRole !== undefined) {
        if (val.tier !== 'custom') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['sourceRole'],
                message: 'sourceRole is only permitted when tier is "custom"',
            });
        }
        else if (val.sourceRole.includes('/')) {
            // CTSK-312 Slice 2 — qualified <nest-slug>/<handle> shape. The
            // field-level regex above already enforced the charset/shape;
            // there is no ROLE_CATALOG entry to resolve against (skip
            // getRoleByHandle — that check is global-only) and no
            // forcedSourceTag concept for nest templates, so sourceTag
            // stays the caller's free choice here. Existence of the
            // referenced nest + template is checked server-side at mint
            // time (apps/api/src/routes/pats.ts), not in this schema.
        }
        else {
            const role = getRoleByHandle(val.sourceRole);
            if (!role || role.handle === 'custom') {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['sourceRole'],
                    message: `sourceRole must be a ROLE_CATALOG handle other than "custom" — got "${val.sourceRole}". ` +
                        'See GET /api/v1/role-catalog for the current catalog.',
                });
            }
            else if (role.forcedSourceTag !== undefined) {
                // CTSK-373 follow-up (2026-08-21): a role with forcedSourceTag
                // represents ONE fixed shared identity — sourceTag is not the
                // client's free choice here. Reject rather than silently
                // override so a caller who typed a different tag on purpose
                // gets a clear error instead of a mint that quietly ignored
                // what they asked for. Omitting sourceTag entirely is fine —
                // the mint route (apps/api/src/routes/pats.ts) defaults it to
                // role.forcedSourceTag in that case.
                if (val.sourceTag !== undefined && val.sourceTag !== role.forcedSourceTag) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['sourceTag'],
                        message: `sourceTag is fixed to "${role.forcedSourceTag}" for role "${role.handle}" — ` +
                            'omit this field to use it automatically, or pick a different role if you need a custom tag.',
                    });
                }
            }
        }
    }
});
// Re-export the canonical capability list so callers (e.g. the Settings
// UI scope picker) can render the checkbox grid without a separate import.
export const PAT_SCOPE_VALUES = CAPABILITIES;
//# sourceMappingURL=pat.js.map